"use client";

import { Menu, X } from "lucide-react";
import Image from "next/image";
import NextLink from "next/link";
import { useState } from "react";
import { PublicLink as Link } from "@/components/public/PublicLink";
import { Button } from "@/components/ui/button";
import { LanguageSwitcherButton } from "@/components/ui/language-switcher";
import { ThemeSwitcher } from "@/components/ui/theme-switcher";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";
import { useUser } from "@/hooks/useUser";

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useJsonTranslation("Header");
  const { user, loading } = useUser();

  const navLinks = [
    { labelKey: "home.nav.features", href: "/features" },
    { labelKey: "home.nav.pricing", href: "/seeds" },
    { labelKey: "home.nav.howItWorks", href: "/how-to" },
    { labelKey: "home.nav.download", href: "/download" },
    { labelKey: "home.nav.blog", href: "/blog" },
    { labelKey: "home.nav.movieArchitect", href: "https://moviearchitect.ai" },
  ];

  return (
    <header className="fixed top-0 right-0 left-0 z-50 pt-[env(safe-area-inset-top)]">
      <div className="home-nav-shell">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex h-16 items-center justify-between gap-4">
            {/* Logo */}
            <Link
              href="/"
              className="focus-visible:ring-ring flex items-center gap-2 rounded-lg focus-visible:ring-2 focus-visible:outline-none"
            >
              <Image
                src="/logo-icon.png"
                alt="VidSeeds.ai"
                width={28}
                height={28}
                className="h-7 w-auto"
                priority
              />
              <span className="font-display text-foreground text-xl font-bold">
                <span className="text-leaf-600 dark:text-leaf-400">Vid</span>
                Seeds
                <span className="text-green-500">.ai</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden items-center gap-1 md:flex">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="home-ghost-link"
                >
                  {t(link.labelKey)}
                </Link>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden items-center gap-3 md:flex">
              <ThemeSwitcher />
              <LanguageSwitcherButton />
              {!loading &&
                (user ? (
                  <Button asChild size="sm" className="home-outline-action">
                    <NextLink href="/dashboard">
                      {t("home.header.dashboard")}
                    </NextLink>
                  </Button>
                ) : (
                  <>
                    <Button asChild variant="ghost" size="sm">
                      <Link href="/login">
                        {t("home.header.login")}
                      </Link>
                    </Button>
                    <Button asChild size="sm" className="home-outline-action">
                      <Link href="/signup">
                        {t("home.header.startTrial")}
                      </Link>
                    </Button>
                  </>
                ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              type="button"
              className="glass glass-hover text-muted-foreground hover:text-foreground focus-visible:ring-ring rounded-xl p-2 focus-visible:ring-2 focus-visible:outline-none md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={t("home.header.menuToggle")}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="border-border/40 border-t py-4 md:hidden">
              <nav className="flex flex-col gap-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="home-ghost-link"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t(link.labelKey)}
                  </Link>
                ))}
                <div className="border-border/40 mt-2 flex flex-col gap-2 border-t pt-4">
                  <div className="flex items-center gap-2 px-2">
                    <ThemeSwitcher />
                    <LanguageSwitcherButton />
                  </div>
                  {!loading &&
                    (user ? (
                      <Button asChild className="home-outline-action w-full">
                        <NextLink
                          href="/dashboard"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {t("home.header.dashboard")}
                        </NextLink>
                      </Button>
                    ) : (
                      <>
                        <Button asChild variant="ghost" className="w-full">
                          <Link
                            href="/login"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {t("home.header.login")}
                          </Link>
                        </Button>
                        <Button asChild className="home-outline-action w-full">
                          <Link
                            href="/signup"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {t("home.header.startTrial")}
                          </Link>
                        </Button>
                      </>
                    ))}
                </div>
              </nav>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
